import { useEffect, useRef } from 'react';

/** One plotted line: y-values + a CSS colour. */
export type Series = { values: number[]; color: string };

/**
 * Compact canvas line chart with a translucent area fill — matches the RTM
 * mockup's `drawRTMLineChart` / hosting sparklines (gradient fill under a
 * 1.5px stroke). Used across the RTM Services view (ClickUp 86e1w8ude).
 *
 * Multiple series share one y-scale. `min`/`max` may be fixed (e.g. uptime
 * 99–100%); otherwise the scale is derived from the data with headroom.
 * The first series gets the area fill; others are stroked only.
 */
export function MiniLineChart({
  series,
  height = 48,
  min,
  max,
  fill = true,
  className,
}: {
  series: Series[];
  height?: number;
  min?: number;
  max?: number;
  fill?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = canvas.parentElement?.clientWidth ?? canvas.clientWidth ?? 200;
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.round(height * dpr);
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, height);

    const all = series.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
    if (all.length === 0) return;
    const lo = min ?? Math.min(...all);
    const hiRaw = max ?? Math.max(...all);
    const hi = hiRaw > lo ? hiRaw : lo + 1;
    const pad = 4;
    const usableH = height - pad * 2;

    const xy = (vals: number[], i: number) => {
      const n = Math.max(1, vals.length - 1);
      const x = (i / n) * w;
      const t = (vals[i]! - lo) / (hi - lo);
      const y = pad + (1 - Math.max(0, Math.min(1, t))) * usableH;
      return [x, y] as const;
    };

    series.forEach((s, si) => {
      const vals = s.values.filter((v) => Number.isFinite(v));
      if (vals.length < 2) return;
      if (fill && si === 0) {
        ctx.beginPath();
        vals.forEach((_, i) => {
          const [x, y] = xy(vals, i);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.lineTo(w, height);
        ctx.lineTo(0, height);
        ctx.closePath();
        ctx.fillStyle = hexToRgba(s.color, 0.1);
        ctx.fill();
      }
      ctx.beginPath();
      vals.forEach((_, i) => {
        const [x, y] = xy(vals, i);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.stroke();
    });
  }, [series, height, min, max, fill]);

  return (
    <canvas ref={ref} className={className} style={{ width: '100%', display: 'block', height }} />
  );
}

/** `#rrggbb` → `rgba(r,g,b,a)`. Falls back to the input for non-hex. */
function hexToRgba(hex: string, a: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = Number.parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
